import React, { useMemo, useRef, useEffect, useCallback, useState } from 'react';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import * as Highcharts from 'highcharts';
import { LegendOptions } from 'highcharts';
import { Formik, Form } from 'formik';

import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { Theme } from '@material-ui/core/styles/createTheme';
import TextField from '@material-ui/core/TextField';
import MenuItem from '@material-ui/core/MenuItem';

import { fractionToPercent, sqlDateToYearAndMonth, isoDateTimeToLabel } from 'helpers/helpers';
import { chartDataDates, total } from 'helpers/chart'; //chartMetadata
import Info from 'components/Tooltip/Info';
import ConfirmDialog from 'components/ConfirmDialog/ConfirmDialog';
import { typeIcon } from 'pages/Accounts/Edit/components/iconHelper';
import FreeSwitch from 'components/FormFields/FreeSwitch';
import { DateSelects } from 'components/FormFields/FormDateSelects';
import CustomPaper from 'components/Layout/Background';
import ChartGraph, { ChartData, ChartTooltipData } from 'components/Graph/ChartGraph';
import FilterMenu from 'components/FilterMenu/FilterMenu';
// import FormCheckboxGroup from 'components/FormCheckboxGroup/FormCheckboxGroup';
import withoutEnterSubmit from 'components/withoutEnterSubmit';

import { WorkflowPlanStore } from 'stores/workflowStores/newWorkflowPlanStore/workflowPlanStore';
import { OverviewStore } from 'stores/account/overviewStore/overviewStore';
import { AccountEditPlan, ChartComputedData } from 'stores/workflowStores/newWorkflowPlanStore/model/interfaces';
import { EdwardColors, Currency, PersonType } from 'enums';
import useStyles from './styles';
import useOverviewStyles from 'pages/Accounts/Detail/Overview/overviewStyles';
import { offsetLabel } from './helpers';
import ChartRange from './ChartRange';
import { assembleEvents } from './helpers';
import Overview from './Overview';

import CollapseChartWrapper from './CollapseChartWrapper';
import { EventType } from 'pages/Accounts/Edit/Plan/Chart/model';

const FormWithoutEnterSubmit = withoutEnterSubmit(Form);

export const extProdColor = EdwardColors.GOLD;
export const loanColor = EdwardColors.ALERT;
export const targetColor = EdwardColors.DARK;
export const depositColor = EdwardColors.GREEN;
export const reserveColor = EdwardColors.SECONDARY;
export const failedDepositColor = EdwardColors.ALERT;
export const altColor = EdwardColors.GRAY_MEDIUM;
export const dateColor = 'rgba(0,0,0,1)';

export const RANGE_HEIGHT = 32;

const legendOptions: LegendOptions = {
  align: 'right',
  layout: 'horizontal',
  verticalAlign: 'bottom',
  floating: false,
  symbolWidth: 18,
  symbolHeight: 18,
  symbolRadius: 2,
  useHTML: true,
  itemStyle: { lineHeight: '10px' },
  itemHiddenStyle: { color: '#666' },
  itemMarginBottom: 8,
};

type StackingOptions = undefined | 'normal';

interface Props {
  planData: AccountEditPlan;
  data: ChartData[];
  metaData: ChartComputedData;
  stacking: StackingOptions;
  chartTooltipData?: ChartTooltipData;
  accountId: string;
  readOnly?: boolean;
  userMode?: boolean;
  dashboard?: boolean;
  overview?: boolean;
  original?: boolean;
  currency: Currency;
  store: WorkflowPlanStore | OverviewStore;
}

const Chart: React.FC<Props> = ({
  planData,
  data,
  metaData,
  stacking,
  chartTooltipData,
  store: workflowPlanStore,
  readOnly,
  userMode,
  dashboard,
  overview,
  original,
  currency,
}) => {
  const { t } = useTranslation();
  const classes = useStyles({ overview });
  const overviewClasses = useOverviewStyles();
  const mobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'), { noSsr: true });

  const domRef = useRef<HTMLDivElement | null>(null);
  const chartRef = useRef<Highcharts.Chart | null>();

  const translatedCurrency = useMemo(() => t(`currency_${currency}`), [t, currency]);
  const inflation = metaData.inflation && metaData.inflationRate;
  const agesDataset = useMemo(() => planData!.chart.dataset!.find((ds) => ds.title === 'client.age'), [planData]);
  const dates = useMemo(() => chartDataDates(planData), [planData]);
  const totals = useMemo(() => total(planData), [planData]);
  const values = useMemo(() => totals.map((t) => t[1]), [totals]);
  //const maxValue = useMemo(() => Math.max(...values), [values]);
  const maxRange = useMemo(
    () => (metaData.showRange ? dates.findIndex((d) => d === metaData.endDate) : dates.length - 1),
    [metaData.showRange, metaData.endDate, dates],
  );

  //  const { current: chart } = chartRef;

  const events = assembleEvents(planData, metaData, dates, totals, currency, classes.hover, mobile, readOnly, original);

  const [period, setPeriod] = useState<undefined | number[]>(undefined);
  const [svgWidth, setSvgWidth] = useState(0);
  const [svgPosX, setSvgPosX] = useState(0);
  //const [svgMaxY, setSvgMaxY] = useState(0);
  //const [svgCommands, setSvgCommands] = useState('');

  const periods = useMemo(
    () => [
      { value: 6, label: 'accountDetail_graph_6months' },
      { value: 12, label: 'accountDetail_graph_year' },
      { value: 36, label: 'accountDetail_graph_3years' },
      { value: 60, label: 'accountDetail_graph_5years' },
      { value: 120, label: 'accountDetail_graph_10years' },
      { value: maxRange, label: 'accountDetail_graph_end' },
    ],
    [maxRange],
  );

  const handlePeriod = (p: number) => {
    setPeriod([0, p <= maxRange ? p : maxRange]);
  };

  const setupLabels = useCallback(
    (visible: boolean) => {
      //console.log('setupLabels');
      const { current: chart } = chartRef;
      const yMax = chart?.yAxis[0].max || 1;
      const xMax = chart?.xAxis[0].max;
      const { current: dom } = domRef;
      const labels = dom ? Array.from(dom.getElementsByClassName('highcharts-plot-line-label')) : [];
      const plotLines = dom ? Array.from(dom.getElementsByClassName('custom-plot-line')) : [];
      const chartAreaWidth = chart?.plotWidth || 1;
      const chartAreaHeight = chart?.plotHeight || 1;
      const tickPx = chartAreaWidth / (xMax || 1);
      const chartStartX = chart?.plotLeft || 0;
      const chartStartY = chart?.plotTop || 0;
      const chartSpacingTop = chart?.options?.chart?.spacingTop || 0;
      const series = chart?.series as (Highcharts.Series & { yData: number[] })[];
      const visibleSeries = series ? series.filter((s) => s.visible === true) : null;
      const totals = series ? Array(series[0].yData.length).fill(0) : [];
      visibleSeries && visibleSeries.forEach((s) => s.yData.forEach((y, i) => (totals[i] += y)));

      setSvgWidth(chartAreaWidth);

      let prevSize: number[] = [],
        prevShift: number[] = [],
        prevPos: number[] = [];

      for (let i = 0; i < labels.length; i++) {
        const id = labels[i].children[0].getAttribute('data-event-id');
        const type = labels[i].children[0].getAttribute('data-event-type');
        const extProduct = labels[i].children[0].getAttribute('data-ext') === 'true';
        const seriesIndex = data.findIndex((ds) => ds.id && ds.id === id);

        const c = offsetLabel(
          labels[i],
          plotLines[i],
          seriesIndex < 0 || series[seriesIndex]?.visible !== false,
          0,
          yMax,
          chartAreaHeight,
          chartSpacingTop,
          chartStartX,
          chartStartY,
          totals,
          tickPx,
          prevSize,
          prevShift,
          prevPos,
        );

        if (visible) {
          if (dom) {
            c.addEventListener('mouseenter', () => {
              dom.setAttribute('class', 'noTooltip');
            });
            c.addEventListener('mouseleave', () => {
              dom.setAttribute('class', '');
            });
          }
          if (
            !readOnly &&
            'setEditTarget' in workflowPlanStore &&
            'setEditRent' in workflowPlanStore &&
            'setEditDeposit' in workflowPlanStore &&
            'setEditReserve' in workflowPlanStore &&
            'setEditExtProduct' in workflowPlanStore
          ) {
            c.addEventListener('click', () => {
              if (extProduct) {
                workflowPlanStore.setEditExtProduct(id);
                return;
              }
              switch (type) {
                case 'TARGET':
                  workflowPlanStore.setEditTarget(id);
                  break;
                case 'PORTFOLIO_RENT':
                  workflowPlanStore.setEditRent(true);
                  break;
                case 'DEPOSIT':
                  workflowPlanStore.setEditDeposit(id);
                  break;
                case 'RESERVE':
                  workflowPlanStore.setEditReserve(id);
                  break;
              }
            });
          }
        }
      }

      setSvgPosX(chartStartX);
      setSvgWidth(chartAreaWidth);
    },
    [domRef, chartRef, readOnly, workflowPlanStore, data], //
  );

  useEffect(() => {
    //console.log('uE setup');
    const { current: chart } = chartRef;
    const indexOfDate = dates.indexOf(metaData.endDate!);
    chart?.xAxis[0].setExtremes(0, indexOfDate);
    setupLabels(true);
  }, [readOnly, metaData.endDate, domRef, dates, setupLabels]); //composeEvents, chartDataDates,

  const xPlotLines = useMemo(() => events, [events]);

  // FIXME: move to helper
  const xAxisLabelFormatter: Highcharts.FormatterCallbackFunction<Highcharts.AxisLabelsFormatterContextObject<number>> =
    useMemo(
      () =>
        function () {
          if (!agesDataset) {
            return planData?.personType === PersonType.LEGAL
              ? isoDateTimeToLabel(this.value.toString(), 'LL/yyyy')
              : `${this.value}`;
          }

          return `${(agesDataset.detailData && agesDataset.detailData[this.value]) || agesDataset.data[this.value]} ${t(
            'general_years',
          )}`; // need to return a string
        },
      [agesDataset, t, planData?.personType],
    );

  const rangeLabels = useMemo(() => {
    if (agesDataset?.detailData && planData?.personType !== PersonType.LEGAL) {
      return Object.keys(agesDataset.detailData)
        .filter((d, i) => i % 60 === 0 && i < maxRange)
        .map((d, i) => ({ value: i * 60, label: `${agesDataset!.detailData![d]} ${t('general_years')}` }));
    } else {
      return dates
        .filter((d, i) => i % 60 === 0 && i < maxRange)
        .map((d, i) => ({ value: i * 60, label: isoDateTimeToLabel(d, 'yyyy') }));
    }
  }, [agesDataset, t, planData?.personType, maxRange, dates]);

  const setChartDisplay = (date: string) => {
    const { current: chart } = chartRef;
    const indexOfDate = dates.indexOf(date);
    const value = planData.chart.dataset![metaData.totalIndex].data[date];
    const age = planData.chart.dataset![metaData.ageIndex]?.detailData
      ? planData.chart.dataset![metaData.ageIndex].detailData![date]
      : null;
    if (!metaData.displaySetValue) {
      chart?.xAxis[0].setExtremes(0, indexOfDate);
      metaData.endDate = date;
      metaData.endValue = value;
      setPeriod([0, indexOfDate]);
    } else {
      metaData.displayDate = date;
      metaData.displayValue = value;
      metaData.displayAge = age ? age.toString() : null;
    }
    if ('setDisplayDate' in workflowPlanStore) {
      workflowPlanStore.setDisplayDate(date, metaData.displaySetValue);
    }
  };

  const handleRangeChangeInChart = (newValue: number | number[]) => {
    const { current: chart } = chartRef;
    chart?.xAxis[0].setExtremes((newValue as number[])[0], (newValue as number[])[1]);
    setPeriod(newValue as number[]);
  };

  if (!data || !workflowPlanStore.data) {
    return <div />;
  }

  const briefInfo = dashboard || (workflowPlanStore.detailMode && !userMode);

  // console.log('render Chart', Date.now());

  return (
    <>
      {overview && (
        <Typography
          variant="h3"
          className={`${classes.title} ${
            original ? overviewClasses.originalSubtitle : overview ? overviewClasses.proposedSubtitle : ''
          }`}
        >
          {t('accountDetail_overview_chartTitle')}&nbsp;
          {inflation
            ? t('accountDetail_overview_chartTitle_inflationEnabled')
            : t('accountDetail_overview_chartTitle_inflationDisabled')}
        </Typography>
      )}
      {!overview &&
        (!planData.externalProductData?.externalProducts.length || dashboard) && ( //EDS-4891
          <Typography variant="h3" className={classes.title}>
            {t('accountPlan_developmentInTime')}
          </Typography>
        )}
      <CustomPaper
        padding={8}
        style={{ marginTop: '8px', position: 'relative', padding: overview ? '32px 0' : '16px' }}
        inside={!!overview}
      >
        <Overview
          planData={planData}
          metaData={metaData}
          original={original}
          detailMode={workflowPlanStore.detailMode}
          briefInfo={briefInfo}
          currency={currency}
          originalClass={overviewClasses.originalInfo}
        />

        <Box mb={2} className={classes.fixChart}>
          {/* FIXME: remove formik */}
          <Formik
            initialValues={{ filter: [EventType.DEPOSIT, EventType.TARGET, EventType.WITHDRAWAL] }}
            onSubmit={() => {}}
          >
            {(formikProps) => (
              <FormWithoutEnterSubmit>
                <div ref={domRef}>
                  <ChartGraph
                    data={data}
                    currency={currency}
                    translatedCurrency={translatedCurrency}
                    stacking={stacking}
                    allDataToTooltip={chartTooltipData || false}
                    legendOptions={legendOptions}
                    xPlotLines={xPlotLines}
                    xAxisLabelFormatter={xAxisLabelFormatter}
                    xAxisLabelStep={planData.chart.metadata.ageAxisStep}
                    xAxisTitle={
                      planData.personType === PersonType.LEGAL
                        ? ''
                        : `${t('client.age').toLocaleLowerCase()}${
                            planData.chart.metadata.partnerAgePresent ? `/${t('general_partners')}` : ''
                          }`
                    }
                    callback={(chart) => {
                      chartRef.current = chart;
                    }}
                    setupLabels={setupLabels}
                    toggleExtProduct={
                      'toggleExtProd' in workflowPlanStore ? workflowPlanStore.toggleExtProd : undefined
                    }
                  />
                </div>
              </FormWithoutEnterSubmit>
            )}
          </Formik>
        </Box>

        {!overview && (
          <>
            <CollapseChartWrapper initiallyExpanded={false} svgPosX={svgPosX}>
              <ChartRange
                presetRange={period}
                maxRange={maxRange}
                handleRangeChangeInChart={handleRangeChangeInChart}
                //svgPosX={svgPosX}
                svgWidth={svgWidth}
                domRef={domRef.current}
                //svgMaxY={maxValue}
                values={values}
                //svgCommands={svgCommands}
                dates={dates}
                labels={rangeLabels}
                area
                inititalUpdate
              />
              <Grid container spacing={0} alignItems="flex-start" style={{ marginTop: '16px', marginBottom: '24px' }}>
                <Grid item xs={12} style={{ color: EdwardColors.GRAY_MEDIUM }}>
                  {t('accountDetail_movements_period')}:
                </Grid>
                <Grid item xs={12} lg={9} xl={6}>
                  <FilterMenu
                    onClick={handlePeriod}
                    items={periods}
                    currentValue={period ? (period[0] > 0 ? 0 : period[1]) : maxRange}
                    selectText="accountDetail_graph_select"
                  />
                </Grid>
              </Grid>

              {dashboard && (
                <Grid container spacing={4} alignItems="flex-start">
                  {inflation && (
                    <Grid
                      container
                      spacing={4}
                      item
                      xs={12}
                      md={4}
                      alignItems="flex-start"
                      style={{ justifyContent: 'left' }}
                    >
                      <Grid item xs={12} style={{ flexBasis: 'content' }}>
                        <Typography variant={mobile ? 'body2' : 'body1'}>
                          {t('general_Inflation')}: {fractionToPercent(metaData.inflationRate!)}
                          <Info content={t('tooltip_plan_inflationPreview')} inline />
                        </Typography>
                      </Grid>
                    </Grid>
                  )}
                </Grid>
              )}
              {!userMode && !dashboard && (
                <Grid container spacing={4} alignItems="flex-start">
                  {metaData.showRange && planData.chart.metadata.min && planData.chart.metadata.max && (
                    <Grid item xs={12} lg={6}>
                      <Grid container alignItems="center" style={{ justifyContent: 'left' }}>
                        <Grid
                          container
                          item
                          xs={6}
                          sm={4}
                          lg={6}
                          alignItems="center"
                          style={{ flexBasis: 'content', paddingLeft: '16px' }}
                        >
                          {metaData.displaySetValue && (
                            <span className={classes.dateIcon}>{typeIcon('DATE', dateColor)}</span>
                          )}
                          <Typography component="span" variant={mobile ? 'body2' : 'body1'}>
                            {t(metaData.displaySetValue ? 'accountPlan_graphDisplayDate' : 'accountPlan_graphUntil')}
                          </Typography>
                        </Grid>

                        <Grid item xs={6} sm={8} lg={6}>
                          <DateSelects
                            name="maxChartYDateSelect"
                            minDate={sqlDateToYearAndMonth(planData.chart.metadata.min)}
                            maxDate={sqlDateToYearAndMonth(planData.chart.metadata.max)}
                            value={
                              metaData.displaySetValue
                                ? metaData.displayDate || planData.chart.metadata.max
                                : metaData.endDate || planData.chart.metadata.max
                            }
                            customOnChange={setChartDisplay}
                            disabled={readOnly}
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                  )}
                  {'inflationModal' in workflowPlanStore && 'setInflationRate' in workflowPlanStore && (
                    <Grid
                      container
                      item
                      xs={12}
                      lg={metaData.showRange && planData.chart.metadata.min && planData.chart.metadata.max ? 6 : 12}
                      alignItems="flex-start"
                      style={{ justifyContent: 'left' }}
                      spacing={0}
                    >
                      <FreeSwitch
                        id="chart.inflation"
                        label={t('accountPlan_showInGraph_true')}
                        checked={metaData.inflation}
                        afterChange={() => {
                          workflowPlanStore.inflationModal = true;
                        }}
                        tooltipContent={t('tooltip_plan_inflation')}
                        disabled={readOnly}
                        small={mobile}
                      />
                      {metaData.inflation && metaData.inflationRate !== undefined && (
                        <TextField
                          select={true}
                          value={metaData.inflationRate}
                          onChange={(e) => workflowPlanStore.setInflationRate(Number(e.target.value))}
                          disabled={!metaData.inflation || readOnly}
                          label={t('general_annualInflation')}
                          style={{ width: '100px', marginLeft: '8px' }}
                        >
                          {Array(21)
                            .fill(0)
                            .map((_x, i) => (
                              <MenuItem key={i} value={i / 100}>
                                {i} %
                              </MenuItem>
                            ))}
                        </TextField>
                      )}
                    </Grid>
                  )}
                </Grid>
              )}
            </CollapseChartWrapper>
          </>
        )}

        {'inflationModal' in workflowPlanStore && 'switchInflation' in workflowPlanStore && (
          <ConfirmDialog
            noHeader
            isOpen={workflowPlanStore.inflationModal}
            confirm={() => {
              workflowPlanStore.inflationModal = false;
              setTimeout(() => {
                // hide modal first fix
                workflowPlanStore.switchInflation();
              }, 0);
            }}
          >
            <Typography variant="body1">{t(`accountPlan_inflationModal_${metaData.inflation}`)}</Typography>
          </ConfirmDialog>
        )}
      </CustomPaper>
    </>
  );
};

export default React.memo(observer(Chart));
